/**
 * MessageItem Component
 *
 * Individual chat message with markdown support and feedback.
 * Phase 7: Frontend Integration
 *
 * @module MessageItem
 */

'use client';

import { memo, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  IconButton,
  HStack,
  Spinner,
  toaster,
  Button,
  Badge,
  Tooltip,
} from '@axon/ui';
import { useTranslations } from 'next-intl';
import {
  LuThumbsUp,
  LuThumbsDown,
  LuUser,
  LuBot,
  LuCheck,
  LuDownload,
  LuCopy,
  LuLightbulb,
  LuArrowRight,
  LuExternalLink,
} from 'react-icons/lu';
import type { IconType } from 'react-icons';
import { MarkdownContent } from '@/components/shared/MarkdownContent';
import { useRouter } from '@/i18n/navigation';

import { useChatbotContext } from '../providers/ChatbotProvider';
import type { ChatMessage, FeedbackRating } from '../types/chatbot.types';
import { detectEntityLinks, isEntityId } from '../utils/chatbot.link-utils';

import { ActionConfirmationCard } from './ActionConfirmationCard';
import { ActionResultView } from './ActionResultView';
import { WorkflowProgressView } from './WorkflowProgressView';
import { DataTableView } from './DataTableView';
import { EntityLink } from './EntityLink';
import { QuickReplyButtons } from './QuickReplyButtons';

// ============================================================================
// Props
// ============================================================================

interface MessageItemProps {
  message: ChatMessage;
}

// ============================================================================
// Entity Link Inline Rendering (Phase 19B)
// ============================================================================

/**
 * Renders text with entity IDs replaced by EntityLink components.
 * Used by ReactMarkdown custom renderer to make entity IDs clickable in message content.
 */
function renderTextWithEntityLinks(text: string): React.ReactNode {
  const matches = detectEntityLinks(text);
  if (matches.length === 0) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    // Text before the entity ID
    if (match.startIndex > lastIndex) {
      parts.push(text.slice(lastIndex, match.startIndex));
    }
    // Entity link
    parts.push(
      <EntityLink
        key={`${match.id}-${match.startIndex}`}
        entityId={match.id}
        tableName={match.tableName}
      />
    );
    lastIndex = match.endIndex;
  }

  // Remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ============================================================================
// Component
// ============================================================================

/**
 * MessageItem component
 *
 * Renders a single chat message with markdown and feedback.
 *
 * @example
 * <MessageItem message={message} />
 */
/** Validates that a navigation path is a safe relative route (no javascript:, data:, http: etc.) */
function isSafeNavigationPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//');
}

// ============================================================================
// Inline Callout (Batch 1)
// ============================================================================

/**
 * Tone-driven inline callout. Consolidates the three previously-separate
 * hardcoded pastel blocks (insight=blue, tip=green, navigation=purple) into a
 * single `colorPalette`-based render so they share spacing, borders and dark
 * mode behaviour. Kept INLINE on purpose — a shared `ChatCallout` extraction is
 * deferred to Batch 3.
 */
type CalloutTone = 'info' | 'success' | 'navigation';

const CALLOUT_TONE_CONFIG: Record<
  CalloutTone,
  { colorPalette: 'blue' | 'green' | 'purple'; icon: IconType }
> = {
  info: { colorPalette: 'blue', icon: LuLightbulb },
  success: { colorPalette: 'green', icon: LuArrowRight },
  navigation: { colorPalette: 'purple', icon: LuExternalLink },
};

interface InlineCalloutProps {
  tone: CalloutTone;
  children: React.ReactNode;
  onClick?: () => void;
}

function InlineCallout({ tone, children, onClick }: InlineCalloutProps): React.ReactElement {
  const { colorPalette, icon: Icon } = CALLOUT_TONE_CONFIG[tone];
  const interactive = Boolean(onClick);
  return (
    <Flex
      colorPalette={colorPalette}
      marginTop="2"
      paddingX="3"
      paddingY="2"
      bg="colorPalette.subtle"
      borderRadius="md"
      border="1px solid"
      borderColor="colorPalette.muted"
      align="start"
      gap="2"
      cursor={interactive ? 'pointer' : undefined}
      _hover={interactive ? { bg: 'colorPalette.muted' } : undefined}
      onClick={onClick}
    >
      <Box color="colorPalette.fg" flexShrink={0} marginTop="0.5">
        <Icon size={14} />
      </Box>
      <Box fontSize="xs" color="colorPalette.fg" lineHeight="1.4">
        {children}
      </Box>
    </Flex>
  );
}

export const MessageItem = memo(function MessageItem({ message }: MessageItemProps) {
  const { sendFeedback, sendMessage, session, isLoading } = useChatbotContext();
  const t = useTranslations('chatbot');
  const router = useRouter();
  const [isExporting, setIsExporting] = useState(false);

  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming && message.content === '';
  const hasFeedback = message.metadata?.feedback?.rating !== undefined;
  const isDataQuery = message.type === 'data_query';

  // Custom ReactMarkdown components for entity link rendering (Phase 19B)
  // Intercepts paragraph children to replace entity IDs with clickable EntityLink
  const markdownComponents = useMemo(
    () => ({
      p(props: React.JSX.IntrinsicElements['p']) {
        const { children, ...rest } = props;
        const processedChildren = Array.isArray(children)
          ? children.map((child, i) =>
              typeof child === 'string' ? (
                <span key={i}>{renderTextWithEntityLinks(child)}</span>
              ) : (
                child
              )
            )
          : typeof children === 'string'
            ? renderTextWithEntityLinks(children)
            : children;
        return <p {...rest}>{processedChildren}</p>;
      },
    }),
    []
  );

  // Fix Issue 1: Strip markdown pipe-table lines when DataTableView is rendering
  // Fix Issue 5: Detect if content already contains Sources section to avoid duplication
  const { displayContent, contentHasSources } = useMemo(() => {
    let content = message.content;
    let hasSources = false;

    if (content) {
      hasSources = /\*?\*?Sources\*?\*?\s*:/.test(content);
    }

    if (isDataQuery && message.metadata?.tableData && content) {
      // Remove lines that are part of a markdown pipe table (header, separator, data rows)
      content = content
        .split('\n')
        .filter((line) => !line.trim().startsWith('|'))
        .join('\n')
        .trim();
    }

    return { displayContent: content, contentHasSources: hasSources };
  }, [message.content, isDataQuery, message.metadata?.tableData]);

  const canSendFollowUp = (session?.isConnected ?? false) && !isLoading;

  // Handle feedback click
  const handleFeedback = (rating: FeedbackRating) => {
    sendFeedback(message.id, rating);
  };

  // Handle follow-up suggestion click
  const handleFollowUpClick = useCallback(
    async (suggestion: string) => {
      if (!canSendFollowUp) return;
      await sendMessage(suggestion);
    },
    [canSendFollowUp, sendMessage]
  );

  // Handle export click for data query results
  const handleExport = useCallback(async () => {
    if (!isDataQuery || isExporting) return;

    const exportQuery = message.metadata?.originalQuery;
    if (!exportQuery) return;

    setIsExporting(true);
    try {
      const response = await fetch('/api/v1/ai-chatbot/data/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: exportQuery, format: 'excel' }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || 'export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toaster.create({
        title: t('errors.exportFailed'),
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  }, [message.metadata?.originalQuery, isDataQuery, isExporting, t]);

  // Handle copy to clipboard for assistant messages
  const handleCopy = useCallback(async () => {
    if (!message.content || isCopied) return;

    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      toaster.create({
        title: t('copySuccess'),
        type: 'success',
      });
      // Reset after 2 seconds (cleanup on unmount via ref)
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toaster.create({
        title: t('copyError'),
        type: 'error',
      });
    }
  }, [message.content, isCopied, t]);

  return (
    <Flex
      justify={isUser ? 'end' : 'start'}
      marginBottom="2"
      data-testid="chat-message"
      data-role={message.role}
    >
      {/* Avatar for assistant */}
      {!isUser && (
        <Flex
          width="8"
          height="8"
          borderRadius="full"
          colorPalette="blue"
          bg="colorPalette.subtle"
          color="colorPalette.fg"
          align="center"
          justify="center"
          marginRight="2"
          flexShrink={0}
        >
          <LuBot size={16} />
        </Flex>
      )}

      <Box maxWidth="85%">
        {/* Message bubble */}
        <Box
          bg={isUser ? 'blue.600' : 'bg.subtle'}
          color={isUser ? 'white' : 'fg.default'}
          paddingX="4"
          paddingY="2.5"
          borderRadius="lg"
          borderBottomRightRadius={isUser ? 'sm' : 'lg'}
          borderBottomLeftRadius={isUser ? 'lg' : 'sm'}
          boxShadow="sm"
        >
          {/* Message content */}
          {isStreaming ? (
            // No tokens yet — show "thinking" until the first chunk lands.
            <Flex align="center" gap="2">
              <Spinner size="sm" color={isUser ? 'white' : 'blue.500'} />
              <Text fontStyle="italic" opacity="0.8" fontSize="sm">
                {t('thinking')}
              </Text>
            </Flex>
          ) : message.type === 'error' ? (
            <Text colorPalette="red" color={isUser ? 'red.200' : 'colorPalette.fg'} fontSize="sm">
              {message.content}
            </Text>
          ) : (
            <Box position="relative">
              <MarkdownContent
                content={displayContent}
                variant={isUser ? 'inverted' : 'default'}
                components={markdownComponents}
              />
              {/* Blinking caret while tokens are still streaming in (content
                  already present). Sits just after the rendered markdown. */}
              {message.isStreaming && displayContent !== '' && (
                <Box
                  as="span"
                  display="inline-block"
                  width="2px"
                  height="1em"
                  marginTop="-1"
                  verticalAlign="text-bottom"
                  background={isUser ? 'white' : 'fg.default'}
                  css={{
                    animation: 'chatbotCaretBlink 1s step-end infinite',
                    '@keyframes chatbotCaretBlink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0 },
                    },
                    // Respect users who prefer reduced motion (a11y): keep the
                    // caret visible but stop blinking.
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}
                />
              )}
            </Box>
          )}

          {/* Data table for data_query messages (Phase 16) */}
          {isDataQuery && message.metadata?.tableData && (
            <Box marginTop="2">
              <DataTableView tableData={message.metadata.tableData} />
            </Box>
          )}

          {/* Action confirmation card (Phase 18) */}
          {message.type === 'action_confirmation' && message.metadata?.actionConfirmation && (
            <ActionConfirmationCard
              confirmation={message.metadata.actionConfirmation}
              description={message.content}
            />
          )}

          {/* Action result view (Phase 18) */}
          {message.type === 'action_result' && message.metadata?.actionResult && (
            <ActionResultView result={message.metadata.actionResult} message={message.content} />
          )}

          {/* Navigation indicator (Phase 19) */}
          {message.type === 'navigation' && message.metadata?.navigation && (
            <InlineCallout
              tone="navigation"
              onClick={() => {
                const path = message.metadata?.navigation?.path;
                if (path && isSafeNavigationPath(path)) {
                  router.push(path);
                }
              }}
            >
              <Text fontWeight="medium">
                {message.metadata.navigation.label ?? message.metadata.navigation.path}
              </Text>
            </InlineCallout>
          )}

          {/* Workflow progress view (Phase 19F) */}
          {message.type === 'workflow' && message.metadata?.workflow && (
            <WorkflowProgressView workflow={message.metadata.workflow} />
          )}

          {/* Insight card (Phase 16) */}
          {message.metadata?.insight && (
            <InlineCallout tone="info">{message.metadata.insight}</InlineCallout>
          )}

          {/* Proactive tip (Phase 16) */}
          {message.metadata?.proactiveTip && (
            <InlineCallout tone="success">{message.metadata.proactiveTip}</InlineCallout>
          )}

          {/* Clarification options (Phase 16) */}
          {message.metadata?.clarificationOptions &&
            message.metadata.clarificationOptions.length > 0 && (
              <Flex gap="1.5" flexWrap="wrap" marginTop="2">
                {message.metadata.clarificationOptions.map((option, i) => (
                  <Button
                    key={i}
                    size="xs"
                    variant="outline"
                    disabled={!canSendFollowUp}
                    onClick={() => void handleFollowUpClick(option)}
                  >
                    {option}
                  </Button>
                ))}
              </Flex>
            )}

          {/* Sources - skip if content already contains Sources section (DRY: avoid duplication) */}
          {message.metadata?.sources &&
            message.metadata.sources.length > 0 &&
            !contentHasSources && (
              <Box
                marginTop="2"
                paddingTop="2"
                borderTop="1px solid"
                borderColor={isUser ? 'whiteAlpha.300' : 'border.muted'}
              >
                <Text fontSize="xs" opacity="0.7" marginBottom="1">
                  {t('sources')}
                </Text>
                <HStack gap="1.5" flexWrap="wrap">
                  {message.metadata.sources.map((source, i) => {
                    const linkable = !isUser && isEntityId(source);
                    return (
                      <Tooltip key={`${source}-${i}`} content={source} openDelay={200}>
                        <Badge
                          size="sm"
                          variant="subtle"
                          colorPalette={isUser ? 'gray' : 'blue'}
                        >
                          <Text as="span" opacity="0.7" marginRight="1">
                            {i + 1}
                          </Text>
                          {linkable ? (
                            <EntityLink entityId={source} />
                          ) : (
                            <Text as="span" lineClamp={1} maxWidth="40">
                              {source}
                            </Text>
                          )}
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </HStack>
              </Box>
            )}

          {/* Follow-up suggestions as quick reply buttons (Phase 16) */}
          {message.metadata?.followUpSuggestions &&
            message.metadata.followUpSuggestions.length > 0 && (
              <QuickReplyButtons
                suggestions={message.metadata.followUpSuggestions}
                onSuggestionClick={(s) => void handleFollowUpClick(s)}
                disabled={!canSendFollowUp}
              />
            )}
        </Box>

        {/* Feedback buttons (only for assistant messages) */}
        {!isUser && !message.isStreaming && (
          <HStack marginTop="1" gap="1" justify="flex-start">
            {hasFeedback ? (
              <Flex align="center" gap="1" color="fg.muted" fontSize="xs">
                <LuCheck size={12} />
                <Text>{t('feedback.received')}</Text>
              </Flex>
            ) : (
              <>
                <Tooltip content={t('feedback.helpful')} openDelay={200} asChild>
                  <IconButton
                    aria-label={t('feedback.helpful')}
                    icon={<LuThumbsUp size={14} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFeedback('helpful')}
                  />
                </Tooltip>
                <Tooltip content={t('feedback.notHelpful')} openDelay={200} asChild>
                  <IconButton
                    aria-label={t('feedback.notHelpful')}
                    icon={<LuThumbsDown size={14} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFeedback('not_helpful')}
                  />
                </Tooltip>
              </>
            )}

            {/* Copy button for assistant messages */}
            <Tooltip content={t('copyToClipboard')} openDelay={200} asChild>
              <IconButton
                aria-label={t('copyToClipboard')}
                icon={isCopied ? <LuCheck size={14} /> : <LuCopy size={14} />}
                size="sm"
                variant="ghost"
                onClick={handleCopy}
              />
            </Tooltip>

            {/* Export button for data query results */}
            {isDataQuery && (
              <Tooltip content={t('exportData')} openDelay={200} asChild>
                <IconButton
                  aria-label={t('exportData')}
                  icon={isExporting ? <Spinner size="xs" /> : <LuDownload size={14} />}
                  size="sm"
                  variant="ghost"
                  onClick={handleExport}
                  disabled={isExporting}
                />
              </Tooltip>
            )}

            {/* Processing time */}
            {message.metadata?.processingTimeMs && (
              <Text fontSize="xs" color="fg.subtle" marginLeft="2">
                {(message.metadata.processingTimeMs / 1000).toFixed(1)}s
              </Text>
            )}
          </HStack>
        )}

        {/* Timestamp */}
        <Text fontSize="xs" color="fg.subtle" marginTop="0.5" textAlign={isUser ? 'right' : 'left'}>
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </Box>

      {/* Avatar for user */}
      {isUser && (
        <Flex
          width="8"
          height="8"
          borderRadius="full"
          bg="bg.emphasized"
          color="fg.muted"
          align="center"
          justify="center"
          marginLeft="2"
          flexShrink={0}
        >
          <LuUser size={16} />
        </Flex>
      )}
    </Flex>
  );
});

export default MessageItem;
